/* eslint-disable no-restricted-syntax */

'use client';

import type { Key, ReactElement } from 'react';
import React from 'react';
import type { PartialNode } from '@react-stately/collections';
import type { ItemProps, SectionProps } from '@react-types/shared';
import type { ThemeDensity, ThemeSpaces } from '@frameio/vapor-theme';
import { themeDensitySpacingValues } from '@frameio/vapor-theme';
import { listBoxItemVariants } from './components/ListBoxItem';
import { headerVariants } from './components/ListHeader';
import type { ListVariants } from './types';

type Context = { variant: ListVariants; density: ThemeDensity };

/**
 * These components are used by consumers to describe the structure
 * of their list, and they are used by React Aria to build the list
 * collection. The actual rendering of the list is done by `List`
 * and components in /components.
 *
 * Defining them here allows us to specify which props are supported,
 * and to give them more meaningful semantic names beyond just
 * `Section` and `Item` as they are used in React Aria.
 *
 * This code is based heavily on the `Item` and `Section` components available in
 * react-aria: https://github.com/adobe/react-spectrum/blob/main/packages/%40react-stately/collections/src/Item.ts
 */

type ListSectionProps<T> = SectionProps<T> & {
  /** Is the header a sticky header? Only relevant if the `isHeaderVisible` is true. */
  isHeaderSticky?: boolean;
  /** Is the header visible? */
  isHeaderVisible?: boolean;
  /** Fixed header height, in pixels. */
  headerHeight?: number;
  /** Header background color for sticky headers */
  headerBackground?: string;
  /** Provide an optional className */
  className?: string;
  /** Custom component used to render the wrapper. Use carefully. */
  as?: React.ElementType;
  /**
   * A horizontal margin applied to the entire section, including header. Typically
   * if you have sticky headers you'll want the header to be flush with the edge of the
   * list, so you should instead use the `marginX` prop on `ListItem` itself.
   */
  marginX?: number;
  /**
   * You can use this instead of a sectionGap if you're using sticky
   * headers and you'd like a gap between the sections, but for sticky
   * headers to remain flush with the edge of the list.
   */
  paddingEnd?: number;
  /**
   * Render a divider after this section. Pass `true` to use the default Separator component,
   * or pass a custom React element to render a custom divider.
   * The divider will be centered within the List's sectionGap and will respect the section's margins.
   */
  divider?: React.ReactNode | boolean;
};

function ListSection<T>(__props: ListSectionProps<T>): ReactElement<any> {
  // @ts-expect-error FIXME(strictNullChecks)
  return null;
}

ListSection.getCollectionNode = function* getCollectionNode<T>(
  props: ListSectionProps<T>,
  context: Context
): Generator<PartialNode<T>> {
  const { children, title, items } = props;
  const { variant = 'unstyled' } = context;

  const { headerHeight, isHeaderSticky } = headerVariants[variant];

  yield {
    type: 'section',
    props: {
      ...props,
      headerHeight: props.headerHeight ?? headerHeight,
      isHeaderSticky: props.isHeaderSticky ?? isHeaderSticky,
      paddingEnd: props.paddingEnd ?? 0,
      divider: props.divider,
      variant,
    },
    hasChildNodes: true,
    rendered: title,
    'aria-label': props['aria-label'],
    *childNodes() {
      if (typeof children === 'function') {
        // @ts-expect-error FIXME(strictNullChecks)
        for (const item of items) {
          yield {
            type: 'item',
            value: item,
            renderer: children,
          };
        }
      } else {
        const items: PartialNode<T>[] = [];
        React.Children.forEach(children, (child) => {
          items.push({
            type: 'item',
            element: child,
          });
        });

        yield* items;
      }
    },
  };
};

const _ListSection = ListSection as <T>(
  props: ListSectionProps<T>
) => ReactElement<any>;
export { _ListSection as ListSection };

/**
 * ListItem
 */

export type ListItemProps<T> = Omit<ItemProps<T>, 'routerOptions'> & {
  /** Fixed List item height, in pixels. */
  height?: number;
  /** Is this element selectable? */
  isDisabled?: boolean;
  /** Callback when clicked */
  onAction?: (key: Key) => void;
  /** Callback when pressed with mouse or touch, or when keyboard user presses enter with the item selected */
  onPress?: (key: Key) => void;
  /** Provide an optional className */
  className?: string;
  /** Custom component used to render the wrapper. Use carefully. */
  as?: React.ElementType;
  /** Apply a custom horizontal margin relative to the section. */
  marginX?: number;
  /** Specify router options to be passed to the router provider. */
  routerOptions?: any;
};

function ListItem<T>(__props: ListItemProps<T>): ReactElement<any> {
  // @ts-expect-error FIXME(strictNullChecks)
  return null;
}

function isThemeSpaces(value: number): value is ThemeSpaces {
  return value in themeDensitySpacingValues;
}

function getAdaptiveSpacingValue({
  spacing,
  density,
}: {
  spacing?: number;
  density?: ThemeDensity;
}) {
  if (spacing && density && isThemeSpaces(spacing)) {
    return themeDensitySpacingValues[spacing][density];
  }
  return spacing;
}

ListItem.getCollectionNode = function* getCollectionNode<T>(
  props: ListItemProps<T>,
  context: Context
): Generator<PartialNode<T>> {
  const { children, textValue } = props;
  const { variant = 'unstyled', density } = context;

  const { marginX: variantMarginX, height: variantHeight } =
    listBoxItemVariants[variant];

  const heightValue = props.height ?? variantHeight;
  const height = getAdaptiveSpacingValue({ spacing: heightValue, density });
  const marginX =
    props.marginX ??
    getAdaptiveSpacingValue({ spacing: variantMarginX, density });

  yield {
    type: 'item',
    props: {
      ...props,
      marginX,
      height,
      variant,
    },
    rendered: children,
    'aria-label': props['aria-label'],
    hasChildNodes: false,
    textValue,
  };
};

const _ListItem = ListItem as <T>(props: ListItemProps<T>) => ReactElement<any>;
export { _ListItem as ListItem };
